import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookOpen, Lock, Sparkles, GraduationCap, FileCheck, ArrowRight } from "lucide-react";

/**
 * Public free-preview listing for "Master the 1040."
 * Chapters 1-3 are open to anyone; chapters 4-13 unlock with the Student or Bundle plan.
 * The first practice return (Maria the W-2 employee) is also free to try.
 * Keeps the existing /course flow (behind auth, with progress/streak/bookmarks) intact.
 */
export default function Course1040List() {
  const { user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [practice, setPractice] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/course1040"),
      api.get("/course1040/practice"),
    ])
      .then(([ch, pr]) => { setChapters(ch.data); setPractice(pr.data); })
      .finally(() => setLoading(false));
  }, []);

  const isPaid = user && (user.access_status === "active" || user.role === "admin");

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="course-1040-list">
        <div className="mb-10">
          <div className="label-caps text-primary flex items-center gap-2">
            <GraduationCap size={14} /> Individual Track
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none">
            Master the 1040.
            <span className="block text-primary italic">Line by line.</span>
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            13 chapters that walk every line of Form 1040 in plain English. Filing status, dependents,
            wages, self-employment, credits, deductions, and a full guided practice return. Ends with
            a 25-question exam and a printable certificate.
          </p>

          {!isPaid && (
            <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-wrap items-center gap-4" data-testid="course-1040-preview-banner">
              <div className="flex-1 min-w-[220px]">
                <div className="label-caps text-primary flex items-center gap-2">
                  <Sparkles size={12} /> Free preview
                </div>
                <div className="text-sm mt-1">
                  Read <strong>Chapters 1–3</strong> AND try the first <strong>practice return</strong> (Maria, W-2 employee) right now — no login, no card required.
                </div>
              </div>
              <Button asChild data-testid="course-1040-buy-btn" className="min-w-[200px]">
                <Link to="/pricing?plan=student">Enroll — $175 <ArrowRight size={16} className="ml-1" /></Link>
              </Button>
            </div>
          )}

          {isPaid && (
            <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5 flex flex-wrap items-center gap-3" data-testid="course-1040-enrolled-banner">
              <GraduationCap size={20} className="text-primary" />
              <div className="flex-1">
                <div className="font-medium">You're enrolled — all 13 chapters + streak / bookmarks / progress are unlocked.</div>
                <div className="text-sm text-muted-foreground">Continue in the full student dashboard.</div>
              </div>
              <Button asChild variant="outline" data-testid="course-1040-dashboard-link">
                <Link to="/dashboard">Go to your dashboard →</Link>
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading course…</div>
        ) : (
          <>
            <div className="label-caps mb-3 flex items-center gap-2 text-primary"><BookOpen size={14} /> Chapters</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {chapters.map(c => (
                <ChapterCard key={c.id} chapter={c} />
              ))}
            </div>

            <div className="label-caps mb-3 flex items-center gap-2 text-primary"><FileCheck size={14} /> Practice Returns — On the Actual Form 1040</div>
            <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
              Every practice return uses real Form 1040 line numbers so you learn exactly where each number goes. The first case (Maria, W-2 employee) is free to try — no login.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {practice.map(p => (
                <PracticeCard key={p.id} case={p} />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function PracticeCard({ case: p }) {
  const locked = p.locked;
  const Wrapper = locked ? "div" : Link;
  const props = locked ? {} : { to: `/course/1040/practice/${p.id}` };
  return (
    <Wrapper
      {...props}
      className={`block ${locked ? "cursor-not-allowed" : ""}`}
      data-testid={`practice-1040-card-${p.id}`}
    >
      <Card className={`p-5 h-full transition-colors ${locked ? "bg-secondary/30 opacity-70" : "hover:border-primary/40"}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="label-caps text-[10px] text-primary">Practice Return</div>
          {p.free_preview && !locked && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5">FREE PREVIEW</span>}
          {locked && <Lock size={14} className="text-muted-foreground" />}
        </div>
        <div className="font-serif text-base mb-2 leading-tight">{p.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-3">{p.scenario}</div>
      </Card>
    </Wrapper>
  );
}

function ChapterCard({ chapter }) {
  const locked = chapter.locked;
  const Wrapper = locked ? "div" : Link;
  const props = locked ? {} : { to: `/course/1040/chapter/${chapter.id}` };
  return (
    <Wrapper
      {...props}
      className={`block ${locked ? "cursor-not-allowed" : ""}`}
      data-testid={`chapter-1040-card-${chapter.id}`}
    >
      <Card className={`p-5 h-full transition-colors ${locked ? "bg-secondary/30 opacity-70" : "hover:border-primary/40"}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="label-caps text-[10px] text-primary">Chapter {chapter.id}</div>
          {chapter.free_preview && !locked && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5">FREE PREVIEW</span>}
          {locked && <Lock size={14} className="text-muted-foreground" />}
        </div>
        <div className="font-serif text-lg leading-tight mb-2">{chapter.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-3">{chapter.summary}</div>
        <div className="text-[10px] text-muted-foreground mt-3 uppercase tracking-widest">
          {chapter.reading_time} min · {chapter.category}
        </div>
      </Card>
    </Wrapper>
  );
}
